import React, { useEffect, useState } from 'react'
import { gql, useQuery } from '@apollo/client'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Alert, Button, Select, Space, Switch, Table, Typography, message } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'

import Layout from '../App/layout'
import LayoutContentWrapper from '../../components/utility/layoutWrapper'
import ContentLayout from '../../components/utility/layoutContent'

import { apiFileFetch } from '../../helpers/restRequest'
import downLoadFile from '../../helpers/downLoadFile'

const SUPPLIER_PAYMENT_SUMMARY_QUERY = gql`
  query SupplierPaymentSummary($year: Int!, $month: Int, $paidOnly: Boolean) {
    supplierPaymentSummary(year: $year, month: $month, paidOnly: $paidOnly) {
      supplier {
        id
        name
      }
      total {
        value
      }
      monthly {
        value
      }
    }
  }
`

const MONTHS = Array.from({ length: 12 }, (_, index) => index + 1)
const FIRST_YEAR = 2015
const XLSX_CONTENT_TYPE = 'application/xlsx'

const formatAmount = (value) => (
  value ? value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'
)

const parseIntParam = (value) => {
  const number = parseInt(value, 10)
  return Number.isNaN(number) ? undefined : number
}

const readFilters = (search) => {
  const params = new URLSearchParams(search)
  const month = parseIntParam(params.get('month'))

  return {
    year: parseIntParam(params.get('year')) || new Date().getFullYear(),
    month: MONTHS.includes(month) ? month : undefined,
    paidOnly: params.get('paid_only') !== 'false'
  }
}

const yearOptions = () => {
  const lastYear = new Date().getFullYear()
  return Array.from({ length: lastYear - FIRST_YEAR + 1 }, (_, index) => {
    const year = lastYear - index
    return { value: year, label: String(year) }
  })
}

const getColumns = (t, months) => [
  {
    key: 'supplier',
    title: t('supplierPayments:supplier'),
    dataIndex: ['supplier', 'name'],
    fixed: 'left',
    width: 240,
    sorter: (a, b) => (a.supplier.name || '').localeCompare(b.supplier.name || '', 'th'),
    render: (name, row) => <Link to={`/suppliers/${row.supplier.id}`}>{name || `#${row.supplier.id}`}</Link>
  },
  ...months.map((month) => ({
    key: `month-${month}`,
    title: t(`supplierPayments:months.${month}`),
    align: 'right',
    sorter: (a, b) => a.monthly[month - 1].value - b.monthly[month - 1].value,
    render: (row) => formatAmount(row.monthly[month - 1].value)
  })),
  {
    key: 'total',
    title: t('supplierPayments:total'),
    align: 'right',
    fixed: 'right',
    width: 140,
    defaultSortOrder: 'descend',
    sorter: (a, b) => a.total.value - b.total.value,
    render: (row) => <Typography.Text strong>{formatAmount(row.total.value)}</Typography.Text>
  }
]

const renderSummary = (t, months) => (rows) => {
  const sumOf = (pick) => rows.reduce((sum, row) => sum + (pick(row) || 0), 0)

  return (
    <Table.Summary fixed>
      <Table.Summary.Row>
        <Table.Summary.Cell index={0}>
          <Typography.Text strong>{t('supplierPayments:grandTotal')}</Typography.Text>
        </Table.Summary.Cell>
        {months.map((month, index) => (
          <Table.Summary.Cell key={month} index={index + 1} align='right'>
            {formatAmount(sumOf((row) => row.monthly[month - 1].value))}
          </Table.Summary.Cell>
        ))}
        <Table.Summary.Cell index={months.length + 1} align='right'>
          <Typography.Text strong>{formatAmount(sumOf((row) => row.total.value))}</Typography.Text>
        </Table.Summary.Cell>
      </Table.Summary.Row>
    </Table.Summary>
  )
}

const SupplierPaymentsIndexPage = () => {
  const { t } = useTranslation(['supplierPayments', 'common'])
  const history = useHistory()
  const location = useLocation()
  const [downloading, setDownloading] = useState(false)

  const filters = readFilters(location.search)
  const months = filters.month ? [filters.month] : MONTHS

  useEffect(() => { document.title = 'Supplier Payments | OSH CRM' }, [])

  const { data, loading, error } = useQuery(SUPPLIER_PAYMENT_SUMMARY_QUERY, {
    variables: filters,
    fetchPolicy: 'cache-and-network'
  })

  const updateFilters = (changes) => {
    const next = { ...filters, ...changes }
    const params = new URLSearchParams()
    params.set('year', next.year)
    if (next.month) params.set('month', next.month)
    if (!next.paidOnly) params.set('paid_only', 'false')
    history.replace({ pathname: location.pathname, search: `?${params.toString()}` })
  }

  const downloadXlsx = async () => {
    const params = new URLSearchParams({ year: filters.year, paid_only: filters.paidOnly })
    if (filters.month) params.set('month', filters.month)

    setDownloading(true)
    try {
      const blob = await apiFileFetch(
        `/suppliers/payment_summaries.xlsx?${params.toString()}`,
        'GET',
        {},
        false,
        XLSX_CONTENT_TYPE
      )
      if (!(blob instanceof Blob) || blob.type.includes('json')) {
        message.error(t('supplierPayments:downloadFailed'))
        return
      }
      const filename = ['supplier-payments', filters.year, filters.month].filter(Boolean).join('-')
      downLoadFile({ fileURL: URL.createObjectURL(blob), filename: `${filename}.xlsx` })
    } finally {
      setDownloading(false)
    }
  }

  const rows = (data && data.supplierPaymentSummary) || []

  return (
    <Layout>
      <LayoutContentWrapper breadcrumbs={[{ path: '/supplier_payments', name: t('common:supplierPayments') }]}>
        <ContentLayout>
          <Space wrap align='center' style={{ marginBottom: 16 }}>
            <span>{t('supplierPayments:year')}</span>
            <Select
              value={filters.year}
              style={{ width: 110 }}
              options={yearOptions()}
              onChange={(year) => updateFilters({ year })}
            />
            <span>{t('supplierPayments:month')}</span>
            <Select
              allowClear
              value={filters.month}
              placeholder={t('supplierPayments:allMonths')}
              style={{ width: 160 }}
              options={MONTHS.map((month) => ({ value: month, label: t(`supplierPayments:months.${month}`) }))}
              onChange={(month) => updateFilters({ month })}
            />
            <Space>
              <Switch checked={filters.paidOnly} onChange={(paidOnly) => updateFilters({ paidOnly })} />
              <span>{t('supplierPayments:paidOnly')}</span>
            </Space>
            <Button icon={<DownloadOutlined />} loading={downloading} onClick={downloadXlsx}>
              {t('supplierPayments:downloadXlsx')}
            </Button>
          </Space>
          <Typography.Paragraph type='secondary'>
            {filters.paidOnly ? t('supplierPayments:paidOnlyHint') : t('supplierPayments:allVouchersHint')}
          </Typography.Paragraph>
          {error && <Alert type='error' showIcon message={error.message} style={{ marginBottom: 16 }} />}
          <Table
            rowKey={(row) => row.supplier.id}
            size='small'
            bordered
            scroll={{ x: 'max-content' }}
            loading={loading}
            columns={getColumns(t, months)}
            dataSource={rows}
            pagination={false}
            summary={rows.length ? renderSummary(t, months) : undefined}
          />
        </ContentLayout>
      </LayoutContentWrapper>
    </Layout>
  )
}

export default SupplierPaymentsIndexPage
